import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { IonMenu, IonSplitPane } from '@ionic/angular';
import { Observable } from 'rxjs';
import { AppNavLink } from 'src/models/app-nav-link.model';
import { Organization } from 'src/models/organization';
import { Rodeo } from 'src/models/rodeo';
import { AuthenticateService } from 'src/services/authenticate.service';
import { GlobalMenuService } from 'src/services/global-menu-service';
import { OrganizationsService } from 'src/services/organizations.service';
import { PrintService } from 'src/services/print.service';
import { RodeoService } from 'src/services/rodeo.service';
import { UsersService } from 'src/services/users.service';
@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit {
  @ViewChild('splitPane') splitPane: IonSplitPane;
  @ViewChild('sideMenu') sideMenu: IonMenu;
  hideTitle = false;
  public pageTitle: Observable<string>;
  showMenu = true;
  printMode = false;
  orgId = 0;
  rodeoId = 0;
  showFab = false;
  selectedOrg: Organization;
  public appPages: AppNavLink[] = [
    { title: 'Events', url: '/rodeos', icon: 'flag' },
    { title: 'Settings', url: '/payments/settings', icon: 'settings' },
    { title: 'Organizations', url: '/organizations', icon: 'people' },
    { title: 'Points', url: '/points', icon: 'trophy' }
  ];
  constructor(
    private authService: AuthenticateService,
    private userService: UsersService,
    private orgService: OrganizationsService,
    private rodeoService: RodeoService,
    private router: Router,
    private route: ActivatedRoute,
    private printService: PrintService,
    private globalMenuService: GlobalMenuService) { }

  async ngOnInit() {
    this.route.queryParamMap.subscribe(async map => {
      await this.handleToken(map);
    });

    if (location.pathname.includes('video-embed') || location.pathname.includes('leaderboards-embed') || location.pathname.includes('mobile-app')) {
      // this is an embed, don't run any app setup code and hide menu
      setTimeout(() => {
        this.showFab = false;
        this.showMenu = false;
        this.sideMenu.close();
        this.splitPane.disabled = true;
        this.hideTitle = true;
      }, 250);
      return;
    } else {
      this.showFab = true;
    }

    this.buildAppPages();

    this.pageTitle = this.globalMenuService.pageTitle.asObservable();

    this.orgService.selectedOrg.subscribe(org => {
      this.selectedOrg = org;
      this.handleSelection(org, this.appPages.findIndex(a => a.title === 'Organizations'), 'organizationId');
    });

    this.rodeoService.selectedRodeo.subscribe((rodeo) => {
      this.handleSelection(rodeo, 0, 'rodeoId');
    });

    await this.authService.hasCookieOrToken();
    this.authService.isLoggedIn.subscribe((isLoggedIn) => {
      console.log(isLoggedIn);
      this.showMenu = isLoggedIn;

      if (isLoggedIn) {
        this.authService.getToken()
          .then(async token => {
            try {
              if (token) {
                await Promise.all([
                  this.userService.getAccount(token.id).then(user => this.authService.currentUser.next(user)),
                  // this.setOrg()
                ]);
              }
            } catch (err) {
              console.error('could not load current user', err);
            }
          });
      }
    });

    this.printService.printMode.subscribe((printMode) => {
      this.printMode = printMode;
      console.log(this.printMode);
    });
  }

  async logout() {
    await this.authService.logout();
    this.showMenu = false;
  }

  toggleMenu() {
    this.showMenu = !this.showMenu;
  }

  private async handleToken(map: ParamMap) {
    const queryToken = map.get('token');
    console.log(queryToken);
    if (queryToken) {
      await this.authService.setToken(JSON.parse(queryToken));
      let queryParams = {
        token: null,
      } as any;
      map.keys.forEach((key) => {
        if (key != 'token') queryParams[key] = map.get(key);
      });
      if (!this.router.url.indexOf('payments')) {
        await this.router.navigate(['payments'], {
          queryParams,
          queryParamsHandling: 'merge',
        });
      } else {
        await this.router.navigate(this.route.snapshot.url, {
          queryParams,
          queryParamsHandling: 'merge',
        });
      }
    }
  }

  private handleSelection(
    item: Rodeo | Organization | undefined,
    pageIndex: number,
    key: 'rodeoId' | 'organizationId',
  ) {
    console.log(item, pageIndex, key);

    const menuItem = this.appPages[pageIndex];
    if (item) {
      menuItem.navParam = {};
      menuItem.navParam[key] = item.id;
      menuItem.showChildPages = true;
      menuItem.subtitle = item.name;
    } else {
      menuItem.navParam = undefined;
      menuItem.showChildPages = false;
      menuItem.subtitle = '';
    }
  }

  private buildAppPages() {
    this.appPages = [
      {
        title: 'Events',
        url: '/rodeos',
        icon: 'menu',
        childPages: [
          { title: 'Entries', url: '/rodeos/entries', icon: '' },
          { title: 'Change Requests', url: '/rodeos/requests/dashboard', icon: '' },
          { title: 'Nominations', url: '/rodeos/nominations', icon: ''},
          { title: 'Contestants', url: '/rodeos/contestants', icon: '' },
          { title: 'Payments', url: '/rodeos/payments', icon: '' },
          { title: 'Reports', url: `/rodeos/reports`, icon: '' },
          { title: 'Scoring', url: `/rodeos/scoring`, icon: '' },
          { title: 'Settings', url: '/rodeos/settings', icon: '' }
        ],
        showChildPages: this.rodeoId && this.rodeoId > 0 ? true : false,
        navParam: this.rodeoId > 0 ? { rodeoId: this.rodeoId } : undefined
      },
      // { title: 'Management', url: '/payments/settings', icon: 'settings' },
      {
        title: 'Organizations',
        url: '/organizations',
        icon: 'people',
        childPages: [
          { title: 'General', url: `/organizations/details`, icon: '' },
          { title: 'Memberships', url: `/organizations/memberships`, icon: '' },
          { title: 'Customers', url: `/organizations/customers`, icon: '' },
          { title: 'Media', url: `/organizations/media`, icon: '' },
          { title: 'Staff', url: `/organizations/staff`, icon: '' },
          { title: 'Reports', url: `/organizations/reports`, icon: '' },
          { title: 'Point series', url: `/organizations/pointseries`, icon: '' },
          { title: 'Leaderboards', url: `/organizations/leaderboards`, icon: '' },
          { title: 'Change Requests', url: `/organizations/requests/dashboard`, icon: '' },
        ],
        showChildPages: this.orgId && this.orgId > 0 ? true : false,
        navParam: this.orgId > 0 ? { organizationId: this.orgId } : undefined
      },
      {
        title: 'Points',
        url: '/points',
        icon: 'trophy',
        childPages: [],
        showChildPages: false,
      }
    ];

    console.log(this.appPages);
  }

  // private async setOrg() {
  //   const orgs = await this.orgService.getOrganizationsForUser();

  //   if (this.authService.isNgAdmin()) {
  //     this.orgService.selectedOrg.next(orgs.find(x => x.id == 58));
  //   } else {
  //     this.orgService.selectedOrg.next(orgs[0]);
  //   }
  // }
}
